// src/components/AppComponents/NavBar/NavBar.tsx
'use client';

import { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  RiCloseLargeLine,
  RiCustomerService2Line,
  RiMenuLine,
} from '@remixicon/react';
import Gleap from 'gleap';

import { getMenuItems } from './menuItems';
import useOnClickOutside from '@/shared/hooks/useOnClickOutside';
import { cx } from '@UI/lib/utils/cx';
import i18n from '@/shared/i18n/i18n';
import { TUserCustomerType } from '@/shared/types/customer';
import { Button } from '@/components/UI/components/Button';
import { Menu, MenuButton, MenuItems, MenuItem } from '@UI/components/Menu';
import { useUserCustomersStore } from '@/domains/user/userCustomers.store';
import { useUserProfileStore } from '@/domains/user/userProfile.store';
import { useRouteAccess } from '@/shared/hooks/useRouteAccess';

interface MenuNavProps {
  isCollapsed: boolean;
  customerType: TUserCustomerType;
}

const NavBar: React.FC<MenuNavProps> = ({ isCollapsed, customerType }) => {
  const [isActive, setIsActive] = useState(false);
  const [isButtonAvailable, setIsButtonAvailable] = useState(true);
  const userProfile = useUserProfileStore((s) => s.user);
  const selectedCustomer = useUserCustomersStore((s) => s.selectedCustomer);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const refPanel = useRef(null);
  const { isRestrictedView, showKYC } = useRouteAccess(
    userProfile,
    selectedCustomer,
  );
  const menuItems = getMenuItems(customerType, isRestrictedView, showKYC);
  // mobile menu
  useOnClickOutside(refPanel, () => {
    if (isActive) {
      setIsButtonAvailable(false);
      setIsActive(false);
      setTimeout(() => setIsButtonAvailable(true), 200);
    }
  });

  const handleHelpClick = () => {
    Gleap.open();
  };

  return (
    <>
      <Button
        onClick={() => {
          if (isButtonAvailable) setIsActive(!isActive);
        }}
        icon={isActive ? RiCloseLargeLine : RiMenuLine}
        variant="ghost"
        className="block lg:hidden w-10 h-10 flex items-center justify-center"
      />

      <div
        ref={refPanel}
        className={cx(
          'mobilePanel py-4 px-8 max-lg:p-4',
          `${isActive ? 'max-lg:block' : 'max-lg:hidden'}`,
        )}
      >
        {menuItems.map(({ label, icon, path, submenu }) => (
          <div key={label} className="mb-2">
            {submenu ? (
              <Menu as="div" className="relative">
                {({ open }) => (
                  <>
                    <MenuButton as="div">
                      <Button
                        icon={icon}
                        iconPosition="left"
                        variant="ghost"
                        size="md"
                        className={cx(
                          'menuButton',
                          `${isCollapsed ? 'justify-center collapsed' : 'justify-start'}`,
                          `${open && isCollapsed ? 'selected' : ''}`,
                        )}
                      >
                        {isCollapsed || label}
                      </Button>
                    </MenuButton>
                    <MenuItems static={!isCollapsed}>
                      <div
                        className={cx('submenuConfig', {
                          /* eslint-disable-next-line */
                          'collapsed': isCollapsed
                        })}
                      >
                        {submenu.map(({ label: sublabel, path: subpath }) => (
                          <MenuItem key={subpath}>
                            <div className={isCollapsed ? 'py-0.5' : 'py-0'}>
                              <Button
                                variant="ghost"
                                size="md"
                                className={cx(
                                  'subMenuButtons justify-start',
                                  `${pathname === subpath ? 'selected' : ''}`,
                                )}
                                onClick={() => navigate(subpath)}
                              >
                                {sublabel}
                              </Button>
                            </div>
                          </MenuItem>
                        ))}
                      </div>
                    </MenuItems>
                  </>
                )}
              </Menu>
            ) : (
              <Button
                icon={icon}
                iconPosition="left"
                variant="ghost"
                size="md"
                className={cx(
                  'menuButton',
                  `${isCollapsed ? 'justify-center collapsed' : 'justify-start'}`,
                  `${pathname === path ? 'selected' : ''}`,
                )}
                onClick={() => path && navigate(path)}
              >
                {isCollapsed || label}
              </Button>
            )}
          </div>
        ))}
        <div className="mb-2">
          <Button
            icon={RiCustomerService2Line}
            iconPosition="left"
            variant="ghost"
            size="md"
            className={cx(
              'menuButton',
              `${isCollapsed ? 'justify-center collapsed' : 'justify-start'}`,
            )}
            onClick={handleHelpClick}
          >
            {isCollapsed || i18n.t('navBar.help')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default NavBar;
